import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { admin } from '../../lib/api/admin.ts'
import type { AgentCommand } from '../../lib/api/admin.ts'
import { AthenaError } from '../../lib/api/client.ts'
import { describeError, isIpPermissionDenied } from '../../lib/api/messages.ts'
import { machines } from '../../lib/api/resources.ts'
import { RELEASE_TTL_DEFAULT_SECONDS } from '../../lib/api/types.ts'
import type { AllowedNetwork, BlockedIp, LearnedIp } from '../../lib/api/types.ts'
import { openCommandIdOf } from '../../lib/commands/semantics.ts'
import { useCommandTracking } from '../../lib/commands/useCommandTracking.ts'
import { formatDayClock } from '../../lib/format.ts'
import {
  cidrProblem,
  commandTarget,
  ipCommandLabel,
  ipOutcome,
  ipProblem,
  isIpCommandKind,
  openIpCommands,
  originLabel,
} from '../../lib/ip/semantics.ts'
import { isAwaitingAgent } from '../../lib/commands/semantics.ts'
import { useAction } from '../../lib/useAction.ts'
import { availabilityOf } from '../../lib/webdefense/semantics.ts'
import { useResource } from '../../lib/useResource.ts'
import { CommandStateBadge, ReleaseStatus } from '../commands/CommandState.tsx'
import { Badge } from '../primitives/Badge.tsx'
import { Button } from '../primitives/Button.tsx'
import { ShortId } from '../primitives/CopyValue.tsx'
import { Dialog } from '../primitives/Dialog.tsx'
import { SelectField, TextField } from '../primitives/Field.tsx'
import { EmptyState, ErrorState, LoadingRow, Notice } from '../primitives/States.tsx'
import { TABLE, TD, TH, THEAD, TR } from '../primitives/table.ts'
import { useToast } from '../primitives/Toast.tsx'
import { TTL_OPTIONS } from './ReleaseHost.tsx'

/** O mínimo que o diálogo precisa saber da máquina — o alerta também a conhece só por id e nome. */
export interface IpMachine {
  id: string
  label: string
}

export type IpMode = 'block' | 'unblock' | 'allow' | 'revoke' | 'challenge'

interface ModeCopy {
  title: string
  targetLabel: string
  textLabel: string
  textRequired: boolean
  placeholder: string
  submit: string
  danger: boolean
}

const COPY: Record<IpMode, ModeCopy> = {
  block: {
    title: 'Bloquear IP',
    targetLabel: 'IP a bloquear',
    textLabel: 'Motivo',
    textRequired: true,
    placeholder: 'ex.: varreu 1.200 portas do servidor web',
    submit: 'Bloquear',
    danger: false,
  },
  unblock: {
    title: 'Desbloquear IP',
    targetLabel: 'IP',
    textLabel: 'Motivo',
    textRequired: false,
    placeholder: 'ex.: scanner autorizado do cliente — ticket INC-4471',
    submit: 'Desbloquear',
    danger: true,
  },
  allow: {
    title: 'Permitir IP ou rede',
    targetLabel: 'IP ou rede (CIDR)',
    textLabel: 'Nota',
    textRequired: true,
    placeholder: 'ex.: rede da filial',
    submit: 'Permitir',
    danger: true,
  },
  revoke: {
    title: 'Retirar permissão',
    targetLabel: 'Rede',
    textLabel: 'Motivo',
    textRequired: false,
    placeholder: 'ex.: filial desativada',
    submit: 'Retirar permissão',
    danger: false,
  },
  challenge: {
    title: 'Pôr em challenge',
    targetLabel: 'IP',
    textLabel: 'Motivo',
    textRequired: true,
    placeholder: 'ex.: visto no SOC tentando /wp-admin',
    submit: 'Pôr em challenge',
    danger: false,
  },
}

type Phase = { kind: 'form' } | { kind: 'tracking'; id: string; initial: AgentCommand | null }

/**
 * Um comando do firewall de IP de UMA máquina (`block-ip`, `unblock-ip`,
 * `allow-ip`, `revoke-allow-ip`). O mesmo diálogo em duas fases do
 * `ReleaseHostDialog`: PEDIDO e ACOMPANHAMENTO por `GET /commands/{id}`.
 *
 * O 202 só quer dizer "na fila". A tela afirma o resultado apenas quando o agente
 * confirma (`executed`/`skipped`) — e então avisa quem chama para reler as listas.
 */
export function IpCommandDialog({
  machine,
  mode,
  target,
  lockTarget = false,
  track = null,
  onClose,
  onSettled,
}: {
  machine: IpMachine
  mode: IpMode
  /** IP/rede já conhecido (uma linha, um alerta). */
  target?: string
  /** O alvo veio de uma linha: não se edita, para o comando levar o MESMO texto dela. */
  lockTarget?: boolean
  /** Abre direto no acompanhamento deste comando (vindo dos pendentes). */
  track?: AgentCommand | null
  onClose: () => void
  /** O agente confirmou: as listas mudaram, quem chama recarrega. */
  onSettled: () => void
}) {
  const [phase, setPhase] = useState<Phase>(
    track === null ? { kind: 'form' } : { kind: 'tracking', id: track.id, initial: track },
  )
  const [round, setRound] = useState(0)
  const copy = COPY[mode]

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={copy.title}
      description={<span className="font-medium text-fg">{machine.label}</span>}
      wide
    >
      {phase.kind === 'form' ? (
        <IpForm
          key={round}
          machine={machine}
          mode={mode}
          target={target}
          lockTarget={lockTarget}
          onAccepted={(command) => setPhase({ kind: 'tracking', id: command.id, initial: command })}
          onAlreadyOpen={(id) => setPhase({ kind: 'tracking', id, initial: null })}
          onCancel={onClose}
        />
      ) : (
        <IpTracking
          key={phase.id}
          machine={machine}
          commandId={phase.id}
          initial={phase.initial}
          onSettled={onSettled}
          onReissue={() => {
            setRound((n) => n + 1)
            setPhase({ kind: 'form' })
          }}
          onClose={onClose}
        />
      )}
    </Dialog>
  )
}

function IpForm({
  machine,
  mode,
  target,
  lockTarget,
  onAccepted,
  onAlreadyOpen,
  onCancel,
}: {
  machine: IpMachine
  mode: IpMode
  target?: string
  lockTarget: boolean
  onAccepted: (command: AgentCommand) => void
  onAlreadyOpen: (id: string) => void
  onCancel: () => void
}) {
  const action = useAction()
  const copy = COPY[mode]
  const [value, setValue] = useState(target ?? '')
  const [text, setText] = useState('')
  const [ttl, setTtl] = useState(String(RELEASE_TTL_DEFAULT_SECONDS))
  const [confirmed, setConfirmed] = useState(false)
  // Uma chave por abertura do formulário, estável entre retries (como no ReleaseHost).
  const idempotencyKey = useRef(crypto.randomUUID()).current

  const isIp = mode === 'block' || mode === 'unblock' || mode === 'challenge'
  // O challenge vai pela emissão genérica, que não tem prazo próprio.
  const hasTtl = mode !== 'challenge'
  const targetProblem = lockTarget ? null : isIp ? ipProblem(value) : cidrProblem(value)
  const denied = isIpPermissionDenied(action.error)
  const textMissing = copy.textRequired && text.trim() === ''
  const needsConfirm = mode === 'unblock'
  const disabled =
    denied || value.trim() === '' || targetProblem !== null || textMissing || (needsConfirm && !confirmed)

  const submit = (): void => {
    if (disabled) return
    action.run(async () => {
      const common = { idempotencyKey, expiresInSeconds: Number(ttl) }
      const note = text.trim()
      const alvo = value.trim()
      try {
        const command =
          mode === 'block'
            ? await machines.blockIp(machine.id, { ip: alvo, reason: note, ...common })
            : mode === 'unblock'
              ? await machines.unblockIp(machine.id, { ip: alvo, reason: note === '' ? undefined : note, ...common })
              : mode === 'allow'
                ? await machines.allowIp(machine.id, { cidr: alvo, note, ...common })
                : mode === 'challenge'
                  ? await admin.enqueueCommand(machine.id, {
                      kind: 'challenge_ip',
                      params: JSON.stringify({ ip: alvo }),
                      reason: note,
                      idempotencyKey,
                      acknowledgeNotDeliverable: false,
                    })
                  : await machines.revokeAllowIp(machine.id, { cidr: alvo, reason: note === '' ? undefined : note, ...common })
        onAccepted(command)
      } catch (cause) {
        // Já há comando aberto para este alvo: leve o operador até ele.
        if (cause instanceof AthenaError && cause.code === 'agent_command_already_open') {
          const id = openCommandIdOf(cause.problem.detail)
          if (id !== null) {
            onAlreadyOpen(id)
            return
          }
        }
        throw cause
      }
    })
  }

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      {mode === 'allow' && <Notice tone="warn">Libera também o que estiver bloqueado dentro da rede.</Notice>}

      <TextField
        label={copy.targetLabel}
        value={value}
        onValueChange={setValue}
        error={targetProblem}
        readOnly={lockTarget}
        required
        mono
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        autoFocus={!lockTarget}
        hint={mode === 'allow' ? 'ex.: 203.0.113.0/24' : undefined}
      />

      <label className="flex flex-col gap-1">
        <span className="text-micro font-medium uppercase tracking-label text-fg-secondary">
          {copy.textLabel}
          {copy.textRequired && <span className="ml-1 text-fg-muted">*</span>}
        </span>
        <textarea
          value={text}
          onChange={(event) => setText(event.target.value)}
          maxLength={512}
          rows={2}
          required={copy.textRequired}
          autoFocus={lockTarget}
          className="w-full border border-line-strong bg-inset px-2 py-1 text-base text-fg placeholder:text-fg-muted"
          placeholder={copy.placeholder}
        />
      </label>

      {hasTtl && (
        <SelectField
          label="Validade do pedido"
          value={ttl}
          onValueChange={setTtl}
          options={TTL_OPTIONS}
          className="max-w-60"
        />
      )}

      {needsConfirm && (
        <label className="flex items-start gap-2 text-sm text-fg-secondary">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(event) => setConfirmed(event.target.checked)}
            className="mt-0.5 size-3.5 shrink-0"
            style={{ accentColor: 'var(--c-accent)' }}
          />
          <span>
            Confirmo que <span className="font-mono font-medium text-fg">{value.trim() || 'este IP'}</span> pode voltar a
            falar com <span className="font-medium text-fg">{machine.label}</span> — e que o agente não o bloqueará mais sozinho.
          </span>
        </label>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-line-subtle pt-3">
        {action.error !== null && (
          <p role="alert" className="mr-auto text-sm leading-snug text-crit">
            {describeError(action.error)}
          </p>
        )}
        <Button variant="ghost" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" variant={copy.danger ? 'danger' : 'primary'} busy={action.busy} disabled={disabled}>
          {copy.submit}
        </Button>
      </div>
    </form>
  )
}

function IpTracking({
  machine,
  commandId,
  initial,
  onSettled,
  onReissue,
  onClose,
}: {
  machine: IpMachine
  commandId: string
  initial: AgentCommand | null
  onSettled: () => void
  onReissue: () => void
  onClose: () => void
}) {
  const toast = useToast()
  const { command, error, refresh } = useCommandTracking(commandId, initial)
  const announced = useRef(false)
  // Só anuncia se a confirmação aconteceu DIANTE do operador: abrir um comando
  // antigo dos pendentes/histórico não é notícia.
  const sawAwaiting = useRef(false)

  const state = command?.state ?? null
  if (state !== null && isAwaitingAgent(state)) sawAwaiting.current = true
  const outcome =
    command === null ? null : ipOutcome(command.kind, command.state, commandTarget(command))
  const confirmed = outcome?.confirmed === true

  useEffect(() => {
    if (!confirmed || announced.current) return
    announced.current = true
    // Reler as listas depois de executed/skipped — pendentes não aparecem nelas.
    onSettled()
    if (sawAwaiting.current) toast.success(`${machine.label}: ${outcome?.headline ?? 'confirmado.'}`)
    // `announced` evita reler/anunciar duas vezes; o resto é estável por confirmação.
  }, [confirmed])

  const canReissue = state === 'expired' || state === 'failed' || state === 'cancelled'

  return (
    <div className="space-y-3">
      {command === null || outcome === null ? (
        error !== null ? (
          <ErrorState error={error} onRetry={refresh} />
        ) : (
          <LoadingRow label="Lendo o comando…" />
        )
      ) : (
        <>
          <ReleaseStatus command={command} outcome={outcome} />
          {error !== null && (
            <Notice tone="warn">
              Não foi possível atualizar o estado: {describeError(error)}{' '}
              <button type="button" className="underline" onClick={refresh}>
                Tentar de novo
              </button>
            </Notice>
          )}
        </>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-line-subtle pt-3">
        <span className="mr-auto font-mono text-micro text-fg-muted">comando {commandId}</span>
        {canReissue && (
          <Button variant="danger" onClick={onReissue}>
            Reemitir
          </Button>
        )}
        <Button variant={confirmed ? 'primary' : 'ghost'} onClick={onClose}>
          Fechar
        </Button>
      </div>
    </div>
  )
}

// ── painel "IPs" da máquina ─────────────────────────────────────────────────

type Sub =
  | {
      kind: 'request'
      mode: IpMode
      target?: string
      lockTarget?: boolean
    }
  | { kind: 'track'; command: AgentCommand }
  | null

const DENIED_HINT = 'Só administradores podem gerenciar IPs.'

/**
 * Painel de IPs de uma máquina: bloqueados, allowlist e aprendidos, mais o que
 * está a caminho. É o estado VISTO PELO ATHENA (`scope: seen_by_athena`).
 *
 * A permissão NÃO se deduz do papel: ela vem do catálogo
 * (`GET /api/admin/commands/catalog?endpointId=`), onde os kinds de IP chegam com
 * `available: false` e `unavailable_reason: agent_command_requires_ip_permission`.
 * O servidor continua sendo a autoridade — o 403 é tratado no diálogo.
 */
export function MachineIpsDialog({
  machine,
  onClose,
}: {
  machine: IpMachine
  onClose: () => void
}) {
  const [sub, setSub] = useState<Sub>(null)

  const listsFetcher = useCallback((signal: AbortSignal) => machines.ips(machine.id, signal), [machine.id])
  const lists = useResource(listsFetcher)

  // Pendentes NÃO entram nas listas: vêm da fila de comandos abertos.
  const openFetcher = useCallback(
    (signal: AbortSignal) =>
      machines.commands(machine.id, { page: 1, pageSize: 50, openOnly: true }, signal),
    [machine.id],
  )
  const open = useResource(openFetcher)

  const catalogFetcher = useCallback(
    (signal: AbortSignal) => admin.commandCatalog(machine.id, signal).catch((cause: unknown) => {
      if (signal.aborted) throw cause
      return null
    }),
    [machine.id],
  )
  const catalog = useResource(catalogFetcher)
  const denied =
    catalog.data?.commands.some(
      (c) =>
        isIpCommandKind(c.kind) &&
        !c.available &&
        c.unavailable_reason === 'agent_command_requires_ip_permission',
    ) ?? false

  // Web Defense: sai do catálogo também — o Athena pode ainda não conhecer o comando.
  const challenge = availabilityOf(catalog.data, 'challenge_ip')

  const reloadLists = lists.reload
  const reloadOpen = open.reload
  const refreshAll = useCallback(() => {
    reloadLists()
    reloadOpen()
  }, [reloadLists, reloadOpen])

  const pending = open.data === null ? [] : openIpCommands(open.data.items)
  const ask = (next: NonNullable<Sub>): (() => void) => () => setSub(next)

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => !isOpen && onClose()}
      title="IPs da máquina"
      description={<span className="font-medium text-fg">{machine.label}</span>}
      wide="xl"
    >
      <div className="space-y-4">
        {denied && <Notice tone="crit">{DENIED_HINT}</Notice>}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            disabled={denied}
            title={denied ? DENIED_HINT : undefined}
            onClick={ask({ kind: 'request', mode: 'block' })}
          >
            Bloquear IP
          </Button>
          <Button
            disabled={denied}
            title={denied ? DENIED_HINT : undefined}
            onClick={ask({ kind: 'request', mode: 'allow' })}
          >
            Permitir IP/rede
          </Button>
          <Button
            disabled={!challenge.available}
            title={challenge.reason ?? undefined}
            onClick={ask({ kind: 'request', mode: 'challenge' })}
          >
            Pôr em challenge
          </Button>
          <Button variant="ghost" onClick={refreshAll} busy={lists.loading}>
            Atualizar
          </Button>
        </div>

        {pending.length > 0 && <PendingList commands={pending} onTrack={(command) => setSub({ kind: 'track', command })} />}

        {lists.error !== null ? (
          <ErrorState error={lists.error} onRetry={lists.reload} />
        ) : lists.data === null ? (
          <LoadingRow />
        ) : (
          <>
            <BlockedSection
              items={lists.data.blocked}
              denied={denied}
              onUnblock={(ip) => setSub({ kind: 'request', mode: 'unblock', target: ip, lockTarget: true })}
            />
            <AllowlistSection
              items={lists.data.allowlist}
              denied={denied}
              onRevoke={(cidr) => setSub({ kind: 'request', mode: 'revoke', target: cidr, lockTarget: true })}
            />
            <LearnedSection
              items={lists.data.learned}
              denied={denied}
              onReblock={(ip) =>
                setSub({ kind: 'request', mode: 'block', target: ip, lockTarget: true })
              }
            />
          </>
        )}
      </div>

      {sub?.kind === 'request' && (
        <IpCommandDialog
          machine={machine}
          mode={sub.mode}
          target={sub.target}
          lockTarget={sub.lockTarget}
          onClose={() => setSub(null)}
          onSettled={refreshAll}
        />
      )}
      {sub?.kind === 'track' && (
        <IpCommandDialog
          machine={machine}
          mode={modeOfKind(sub.command.kind)}
          track={sub.command}
          onClose={() => setSub(null)}
          onSettled={refreshAll}
        />
      )}
    </Dialog>
  )
}

function modeOfKind(kind: string): IpMode {
  return kind === 'unblock_ip'
    ? 'unblock'
    : kind === 'allow_ip'
      ? 'allow'
      : kind === 'revoke_allow_ip'
        ? 'revoke'
        : kind === 'challenge_ip'
          ? 'challenge'
          : 'block'
}

function Section({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  return (
    <section>
      <header className="mb-1 flex flex-wrap items-baseline gap-x-2">
        <h3 className="text-sm font-medium text-fg">{title}</h3>
        <span className="tnum text-micro text-fg-muted">{count}</span>
      </header>
      {children}
    </section>
  )
}

/** Quem fez: a regra (automático) ou o UUID do operador. */
function By({ origin, by }: { origin: string; by: string }) {
  return origin === 'automatic' ? (
    <span className="block max-w-56 truncate font-mono text-micro text-fg-secondary" title={by}>
      {by}
    </span>
  ) : (
    <ShortId id={by} label="operador" />
  )
}

function OriginBadge({ origin }: { origin: string }) {
  return (
    <Badge tone={origin === 'automatic' ? 'warn' : 'neutral'}>{originLabel(origin)}</Badge>
  )
}

function Reason({ text }: { text: string | null }) {
  return text === null ? (
    <span className="text-fg-muted">—</span>
  ) : (
    <span className="block truncate text-fg-secondary" title={text}>
      {text}
    </span>
  )
}

function BlockedSection({
  items,
  denied,
  onUnblock,
}: {
  items: BlockedIp[]
  denied: boolean
  onUnblock: (ip: string) => void
}) {
  return (
    <Section title="Bloqueados" count={items.length}>
      {items.length === 0 ? (
        <EmptyState title="Nenhum IP bloqueado." />
      ) : (
        <table className={TABLE}>
          <thead className={THEAD}>
            <tr>
              <th scope="col" className={TH}>IP</th>
              <th scope="col" className={TH}>Origem</th>
              <th scope="col" className={TH}>Por</th>
              <th scope="col" className={TH}>Desde</th>
              <th scope="col" className={TH}>Motivo</th>
              <th scope="col" className={`${TH} text-right`}>
                <span className="sr-only">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.ip} className={TR}>
                <th scope="row" className={`${TD} font-mono font-normal whitespace-nowrap text-fg`}>{item.ip}</th>
                <td className={TD}><OriginBadge origin={item.origin} /></td>
                <td className={TD}><By origin={item.origin} by={item.by} /></td>
                <td className={`${TD} tnum whitespace-nowrap`}>{formatDayClock(item.since)}</td>
                <td className={`${TD} max-w-0`}><Reason text={item.reason} /></td>
                <td className={`${TD} text-right`}>
                  <Button size="sm" variant="danger" disabled={denied} title={denied ? DENIED_HINT : undefined} onClick={() => onUnblock(item.ip)}>
                    Desbloquear
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  )
}

function AllowlistSection({
  items,
  denied,
  onRevoke,
}: {
  items: AllowedNetwork[]
  denied: boolean
  onRevoke: (cidr: string) => void
}) {
  return (
    <Section title="Allowlist" count={items.length}>
      {items.length === 0 ? (
        <EmptyState title="Allowlist vazia." />
      ) : (
        <table className={TABLE}>
          <thead className={THEAD}>
            <tr>
              <th scope="col" className={TH}>Rede</th>
              <th scope="col" className={TH}>Nota</th>
              <th scope="col" className={TH}>Por</th>
              <th scope="col" className={TH}>Desde</th>
              <th scope="col" className={`${TH} text-right`}>
                <span className="sr-only">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.cidr} className={TR}>
                <th scope="row" className={`${TD} font-mono font-normal whitespace-nowrap text-fg`}>{item.cidr}</th>
                <td className={`${TD} max-w-0`}><Reason text={item.note} /></td>
                <td className={TD}><By origin={item.origin} by={item.by} /></td>
                <td className={`${TD} tnum whitespace-nowrap`}>{formatDayClock(item.since)}</td>
                <td className={`${TD} text-right`}>
                  <Button size="sm" variant="danger" disabled={denied} title={denied ? DENIED_HINT : undefined} onClick={() => onRevoke(item.cidr)}>
                    Retirar permissão
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  )
}

function LearnedSection({
  items,
  denied,
  onReblock,
}: {
  items: LearnedIp[]
  denied: boolean
  onReblock: (ip: string) => void
}) {
  return (
    <Section title="Aprendidos" count={items.length}>
      {items.length === 0 ? (
        <EmptyState title="Nenhum IP aprendido." />
      ) : (
        <table className={TABLE}>
          <thead className={THEAD}>
            <tr>
              <th scope="col" className={TH}>IP</th>
              <th scope="col" className={TH}>Por</th>
              <th scope="col" className={TH}>Desde</th>
              <th scope="col" className={TH}>Motivo</th>
              <th scope="col" className={`${TH} text-right`}>
                <span className="sr-only">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.ip} className={TR}>
                <th scope="row" className={`${TD} font-mono font-normal whitespace-nowrap text-fg`}>{item.ip}</th>
                <td className={TD}><By origin={item.origin} by={item.by} /></td>
                <td className={`${TD} tnum whitespace-nowrap`}>{formatDayClock(item.since)}</td>
                <td className={`${TD} max-w-0`}><Reason text={item.reason} /></td>
                <td className={`${TD} text-right`}>
                  <Button size="sm" disabled={denied} title={denied ? DENIED_HINT : undefined} onClick={() => onReblock(item.ip)}>
                    Bloquear de novo
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Section>
  )
}

/** O que está a caminho: comandos de IP abertos. Não aparecem nas listas até o agente confirmar. */
function PendingList({
  commands,
  onTrack,
}: {
  commands: AgentCommand[]
  onTrack: (command: AgentCommand) => void
}) {
  return (
    <Section title="A caminho" count={commands.length}>
      <ul className="divide-y divide-line-subtle border border-line-subtle">
        {commands.map((command) => (
          <li key={command.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-2 py-1.5 text-sm">
            <span className="text-fg">{ipCommandLabel(command.kind)}</span>
            <span className="font-mono text-fg-secondary">{commandTarget(command) ?? '—'}</span>
            <CommandStateBadge state={command.state} />
            <span className="tnum text-micro text-fg-muted">{formatDayClock(command.requested_at)}</span>
            <Button size="sm" variant="ghost" className="ml-auto" onClick={() => onTrack(command)}>
              Acompanhar
            </Button>
          </li>
        ))}
      </ul>
    </Section>
  )
}
